import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatPrice, readApiError } from "@/lib/format";

const CATEGORY_LABELS = {
  bonsai: "Bonsai",
  succulents: "Succulents",
  terrariums: "Terrariums",
  accessories: "Accessories",
};

// Phase 1.6 — lifestyle image + caption per category, shown beneath the buy CTA
const LIFESTYLE = {
  bonsai: {
    src: "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/b1531dfa478a7e2b687c31f873dc5fc91b5b56a4b54b126046166b0a8ec6d774.png",
    caption:
      "Wired branch by wired branch. Each tree leaves the bench knowing exactly how it grew.",
  },
  succulents: {
    src: "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/9d6290a1a1bfc05431aa19fd8760cdad80cb563303c85d6bda0f9524f43e6277.png",
    caption:
      "A morning mist, not a drowning. Succulents want a coastal touch — light, light, and a little more light.",
  },
  terrariums: {
    src: "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/f5e421529ffdefe35a6c5e08fae3ba0bab5606d51c8e723682fb21e26625caf9.png",
    caption:
      "Built one layer at a time on the studio bench — driftwood, moss, and patience under glass.",
  },
  accessories: {
    src: "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/d27e3052e8d2bd90c3ebefbee5063da2acb35a7738470555223faa91852d370f.png",
    caption:
      "Brass for the patina, ceramic for the weight, coastal stones for the drainage. Honest tools, used daily.",
  },
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const res = await api.get(`/products/${slug}`);
        if (!cancelled) setProduct(res.data);
      } catch (e) {
        if (e?.response?.status === 404) {
          if (!cancelled) setNotFound(true);
        } else if (!cancelled) {
          toast.error("Could not load product", {
            description: readApiError(e),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const buy = async () => {
    setCheckoutLoading(true);
    try {
      const res = await api.post(`/checkout/product`, {
        product_slug: slug,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Checkout failed", { description: readApiError(e) });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const lifestyle = product ? LIFESTYLE[product.category] : null;

  return (
    <PublicLayout>
      <section
        className="mx-auto max-w-6xl px-6 py-16"
        data-testid="product-page"
      >
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-sm text-[#C9BEA8] transition-colors hover:text-[#C9A961]"
          data-testid="product-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to the shop
        </Link>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-[#C9BEA8]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading…
          </div>
        ) : notFound ? (
          <div data-testid="product-not-found" className="py-32 text-center">
            <h2 className="font-serif text-3xl tracking-wide text-[#F5EFE2]">
              Not on the bench right now.
            </h2>
            <p className="mt-2 text-[#C9BEA8]">
              Try another corner of the{" "}
              <Link to="/shop" className="text-[#C9A961] hover:underline">
                shop
              </Link>
              .
            </p>
          </div>
        ) : product ? (
          <div className="mt-10 grid gap-12 md:grid-cols-2">
            <div className="overflow-hidden rounded-2xl border border-[#5E594E] bg-[#4D4940]">
              <img
                src={product.image_url}
                alt={product.name}
                className="aspect-square w-full object-cover"
                data-testid="product-image"
              />
            </div>
            <div className="space-y-6">
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.24em] text-[#C9A961]">
                  {CATEGORY_LABELS[product.category] || product.category}
                </p>
                <h1
                  className="font-serif text-4xl tracking-wide text-[#F5EFE2] sm:text-5xl"
                  data-testid="product-name"
                >
                  {product.name}
                </h1>
                <p
                  data-testid="product-price"
                  className="font-serif text-3xl text-[#C9A961]"
                >
                  {formatPrice(product.price_cents)}
                </p>
              </div>

              <div
                data-testid="product-description"
                className="whitespace-pre-line text-base leading-relaxed text-[#F5EFE2]/85"
              >
                {product.description}
              </div>

              <div className="space-y-3 pt-2">
                <Button
                  data-testid="product-buy-btn"
                  onClick={buy}
                  disabled={checkoutLoading}
                  size="lg"
                  className="w-full bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A] sm:w-auto"
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Opening Stripe…
                    </>
                  ) : (
                    `Buy now — ${formatPrice(product.price_cents)}`
                  )}
                </Button>
                <p className="text-xs text-[#7A8B6F]">
                  Stripe-hosted checkout. Test card{" "}
                  <span className="font-mono text-[#C9BEA8]">
                    4242 4242 4242 4242
                  </span>{" "}
                  works end-to-end.
                </p>
              </div>

              <div className="rounded-xl border border-dashed border-[#5E594E] bg-[#4D4940] p-5 text-sm text-[#F5EFE2]/85">
                <p className="font-medium text-[#C9A961]">
                  Hands-on, not shipped from a warehouse.
                </p>
                <p className="mt-1 text-[#C9BEA8]">
                  Every order leaves the bench wrapped in newsprint with a
                  hand-written care note. Ships within a week.
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Lifestyle slab per product category */}
        {product && lifestyle && (
          <div
            data-testid="product-lifestyle"
            className="mt-20 overflow-hidden rounded-2xl border border-[#5E594E] bg-[#4D4940]"
          >
            <img
              src={lifestyle.src}
              alt={`${CATEGORY_LABELS[product.category] || product.category} — studio process`}
              className="aspect-[16/7] w-full object-cover"
              loading="lazy"
            />
            <p className="px-6 py-5 text-center font-serif text-base italic text-[#C9BEA8]">
              {lifestyle.caption}
            </p>
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
